/**
 * WordDefinitionSheet – bottom sheet that opens when a word is tapped anywhere
 * in the app (transcripts, Repeat cards, Review suggestions).
 *
 * Shows: the word, part of speech, translation, friendly definition, syllable
 * breakdown with the stressed syllable highlighted, phonetic respelling, IPA,
 * and an example sentence. A big "Hear it" button plays TTS.
 */
import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  SpeakerHigh, SpinnerGap, X, BookmarkSimple, Info,
} from '@phosphor-icons/react';
import { http, API, apiErrorMessage, getStoredToken } from '@/lib/api';

export default function WordDefinitionSheet({ open, term, context, onClose, practiceDirection }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [playing, setPlaying] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!open || !term) return;
    setData(null);
    setError('');
    setSaved(false);
    setLoading(true);
    (async () => {
      try {
        const { data: d } = await http.post('/define', {
          term,
          context: context || '',
          practice_direction: practiceDirection,
        });
        setData(d);
      } catch (e) {
        setError(apiErrorMessage(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [open, term, context, practiceDirection]);

  async function speakTerm(text) {
    if (!text) return;
    setPlaying(true);
    try {
      const resp = await fetch(`${API}/tts`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(getStoredToken() ? { Authorization: `Bearer ${getStoredToken()}` } : {}),
        },
        body: JSON.stringify({ text }),
      });
      if (!resp.ok) throw new Error('TTS failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onended = () => { URL.revokeObjectURL(url); setPlaying(false); };
      audio.onerror = () => { URL.revokeObjectURL(url); setPlaying(false); };
      audio.play().catch(() => setPlaying(false));
    } catch { setPlaying(false); }
  }

  async function saveToVocab() {
    if (!data?.term) return;
    try {
      await http.post('/vocabulary/save', {
        items: [{
          term: data.term,
          translation: data.translation,
          example: data.example_target || '',
        }],
      });
      setSaved(true);
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }

  return (
    <AnimatePresence>
      {open ? (
        <>
          <motion.div
            className="fixed inset-0 bg-black/40 z-40"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="fixed bottom-0 left-0 right-0 z-50 mx-auto max-w-md rounded-t-3xl bg-[var(--lf-surface)] p-5 pb-8 shadow-2xl"
            initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 280 }}
            data-testid="word-def-sheet"
          >
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">Dictionary</div>
                <div className="text-2xl font-bold" data-testid="word-def-term">{data?.term || term}</div>
                {data?.part_of_speech ? (
                  <div className="mt-1 inline-block rounded-full bg-[var(--lf-surface-muted)] px-2 py-0.5 text-[11px] text-[var(--lf-secondary-foreground)]">
                    {data.part_of_speech}
                  </div>
                ) : null}
              </div>
              <button onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full bg-[var(--lf-surface-muted)]" data-testid="word-def-close" aria-label="Close definition">
                <X size={16} weight="bold" aria-hidden="true" />
              </button>
            </div>

            {loading ? (
              <div className="flex items-center gap-2 text-sm text-[var(--lf-text-muted)]" data-testid="word-def-loading">
                <SpinnerGap size={16} className="animate-spin" /> Looking it up…
              </div>
            ) : error ? (
              <div className="text-sm text-[var(--lf-destructive)]">{error}</div>
            ) : data ? (
              <>
                {/* Translation */}
                <div className="rounded-2xl bg-[#FFF5EE] p-3">
                  <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">Means</div>
                  <div className="text-lg font-semibold text-[var(--lf-text)]" data-testid="word-def-translation">{data.translation}</div>
                  {data.definition ? (
                    <div className="mt-1 text-sm text-[var(--lf-text-muted)]">{data.definition}</div>
                  ) : null}
                </div>

                {/* Pronunciation */}
                {(data.syllables?.length || data.pronunciation_respelling || data.ipa) ? (
                  <div className="mt-3 rounded-2xl bg-[var(--lf-surface-muted)] p-3" data-testid="word-def-pronunciation">
                    <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">Pronunciation</div>
                    {data.syllables?.length ? (
                      <div className="mt-1 flex flex-wrap items-center gap-1 text-xl font-bold">
                        {data.syllables.map((syl, i) => (
                          <span key={i} className={i === data.stress_syllable_index ? 'text-[var(--lf-primary)] underline decoration-[var(--lf-primary)] decoration-2 underline-offset-4' : ''}>
                            {syl}{i < data.syllables.length - 1 ? <span className="opacity-40 mx-0.5">·</span> : null}
                          </span>
                        ))}
                      </div>
                    ) : null}
                    {data.pronunciation_respelling ? (
                      <div className="mt-1 text-sm">
                        <span className="text-[var(--lf-text-muted)]">Say it like:</span>{' '}
                        <span className="font-semibold">{data.pronunciation_respelling}</span>
                      </div>
                    ) : null}
                    {data.ipa ? (
                      <div className="mt-0.5 text-xs text-[var(--lf-text-muted)]">
                        IPA: <span className="font-mono">/{data.ipa}/</span>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {/* Example */}
                {data.example_target ? (
                  <div className="mt-3 rounded-2xl border border-[var(--lf-border)] p-3" data-testid="word-def-example">
                    <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">Example</div>
                    <div className="mt-1 italic">“{data.example_target}”</div>
                    {data.example_native ? (
                      <div className="mt-0.5 text-xs text-[var(--lf-text-muted)]">{data.example_native}</div>
                    ) : null}
                  </div>
                ) : null}

                {/* Actions */}
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={() => speakTerm(data.term)}
                    disabled={playing}
                    className="lf-btn lf-btn-secondary flex-1"
                    data-testid="word-def-play-btn"
                  >
                    {playing ? <SpinnerGap size={14} className="animate-spin" /> : <SpeakerHigh size={14} weight="fill" />}
                    Hear it
                  </button>
                  <button
                    onClick={saveToVocab}
                    disabled={saved}
                    className="lf-btn lf-btn-primary flex-1"
                    data-testid="word-def-save-btn"
                  >
                    <BookmarkSimple size={14} weight="fill" />
                    {saved ? 'Saved' : 'Save'}
                  </button>
                </div>

                <div className="mt-3 flex items-center gap-1 text-[11px] text-[var(--lf-text-muted)]">
                  <Info size={10} /> Tap any other word to look it up
                </div>
              </>
            ) : null}
          </motion.div>
        </>
      ) : null}
    </AnimatePresence>
  );
}
